import { IColumnDefinition } from '@common/types';
import { t } from 'i18next';
import { Box, Button, Link, Typography } from '@mui/material';
import { ConfirmationModal } from '@components';
import { Add, ArrowBack, Edit, InsertDriveFile } from '@mui/icons-material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { theme } from '@store';
import {
  ReportsFolderPreviewTable,
  ReportsFolderPreviewTableFilters,
} from '@views/pages/Reporting/ReportsFolderPreview/components';
import { useReportsFolderPreview } from '@views/pages/Reporting/ReportsFolderPreview/hooks';
import { EditReportFolderModal } from '@views/pages/Reporting/components';

export const ReportsFolderPreview = () => {
  const {
    filters,
    entries,
    handleFilterChange,
    totalCount,
    loading,
    handleDeleteReport,
    deleteReportIsLoading,
    handleCloseDeleteModal,
    isDeleteModalOpen,
    handleOpenDeleteModal,
    handleMakeReportFavorite,
    makeReportFavoriteIsPending,
    reportIdToFavorite,
    handleBack,
    handleAddReport,
    folderTitle,
    isEditReportFolderModalOpen,
    handleCloseEditReportFolderModal,
    handleOpenEditReportFolderModal,
    folder_id,
    editIsDisabled,
    isInitialFilters,
  } = useReportsFolderPreview();

  const columns: IColumnDefinition[] = [
    {
      id: 'favorite',
      label: '',
      sortable: false,
    },
    {
      id: 'name',
      label: t('reporting.report.table.name'),
      sortable: true,
    },
    {
      id: 'description',
      label: t('reporting.report.table.description'),
      sortable: false,
    },
    {
      id: 'folder_name',
      label: t('reporting.report.table.folder'),
      sortable: true,
    },
    {
      id: 'organisation',
      label: t('reporting.report.table.organisation'),
      sortable: false,
    },
    {
      id: 'created_at',
      label: t('reporting.report.table.created_at'),
      sortable: true,
    },
    {
      id: 'private',
      label: '',
      sortable: false,
    },
    {
      id: 'action',
      label: '',
      sortable: false,
    },
  ];

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Link
        underline='always'
        onClick={handleBack}
        sx={{
          display: 'flex',
          justifyContent: 'start',
          alignItems: 'center',
          gap: '8px',
          cursor: 'pointer',
          [theme.breakpoints.down('md')]: {
            display: 'none',
          },
        }}
      >
        <ArrowBackIcon sx={{ fontSize: '1.3rem' }} color='primary' />
        <Typography sx={{ color: 'primary' }}>
          {t('general.backToPrevious')}
        </Typography>
      </Link>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          my: 2,
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Box
            sx={{
              [theme.breakpoints.up('md')]: {
                display: 'none',
              },
              width: 'fit-content',
            }}
          >
            <Link
              underline='always'
              onClick={handleBack}
              sx={{
                gap: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'start',
              }}
            >
              <ArrowBack sx={{ fontSize: '2rem' }} color='primary' />
            </Link>
          </Box>
          <Typography variant='h4'>{folderTitle}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<Edit />}
            sx={{ fontWeight: 'regular', fontSize: '1rem' }}
            variant='outlined'
            onClick={handleOpenEditReportFolderModal}
            disabled={editIsDisabled}
          >
            {t('activityReports.edit')}
          </Button>
          <Button
            startIcon={<Add />}
            sx={{ fontWeight: 'regular', fontSize: '1rem', color: 'white' }}
            color='secondary'
            variant='contained'
            onClick={handleAddReport}
          >
            {t('activityReports.addReport')}
          </Button>
        </Box>
      </Box>
      {isInitialFilters && entries.length === 0 && !loading ? (
        <Box
          sx={{
            height: 'calc(100vh - 310px)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <InsertDriveFile
            sx={{ width: 56, height: 56, mb: 2 }}
            color='primary'
          />
          <Typography variant='h6'>
            {t('reporting.report.table.noResults.title')}
          </Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            {t('reporting.report.table.noResults.subTitle')}
          </Typography>
        </Box>
      ) : (
        <>
          <ReportsFolderPreviewTableFilters
            filters={filters}
            handleFilterChange={handleFilterChange}
          />
          <ReportsFolderPreviewTable
            rows={entries}
            columns={columns}
            filters={filters}
            setFilters={handleFilterChange}
            totalCount={totalCount}
            loading={loading}
            handleOpenDeleteModal={handleOpenDeleteModal}
            handleMakeReportFavorite={handleMakeReportFavorite}
            makeReportFavoriteIsPending={makeReportFavoriteIsPending}
            reportIdToFavorite={reportIdToFavorite}
          />
        </>
      )}

      <ConfirmationModal
        title={t('reporting.report.deleteModal.title')}
        message={t('reporting.report.deleteModal.message')}
        onConfirm={handleDeleteReport}
        onClose={handleCloseDeleteModal}
        isOpen={isDeleteModalOpen}
        isLoading={deleteReportIsLoading}
        confirmText={t('general.delete')}
      />
      <EditReportFolderModal
        isOpen={isEditReportFolderModalOpen}
        handleClose={handleCloseEditReportFolderModal}
        report_folder_id={folder_id}
      />
    </Box>
  );
};
